import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  skipToken,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { api } from '@/api';
import { PaginationSearchParams, SearchQuery, SortOrderQuery, UseQueryConfig } from '@/api/types';
import { PaginatedTXCentersListFilters } from '@/api/apis/facilities';
import { queryClient } from '@/queries';
import { txCentersQueryKeys } from '@/queries/tx-centers/tx-centers.keys';
import { BASE_QUERY_KEYS } from '@/queries/consts';
import { ORGANIZATION_TENANT_PREFIX } from '@/constants/customer.constants';
import { MINUTE_IN_MS } from '@/constants/date.constants';

const allTXCentersPaginationParams: PaginationSearchParams = {
  itemsPerPage: 100,
  pageIndex: 0,
};

const getMyTXCenter = async () => {
  const txCenter = await api.txcenter.getTXCenterById(localStorage.getItem('selectedOrganizationId') || '');

  return txCenter;
};

const getTXCenterByIdWithTXUsers = async (txCenterId: string, useEnabledUsers?: boolean) => {
  const data = await api.txcenter.getTXCenterById(txCenterId);

  if (data) {
    const txUsersList = await api.customer.getCustomerList({
      ...allTXCentersPaginationParams,
      txCenters: txCenterId,
      enabled: useEnabledUsers,
    });

    return { ...data, txUsers: txUsersList.items };
  }

  return data;
};

export const getTXCentersByIds = async (txCentersList: string[]) => {
  const txCentersIdsSet = new Set(txCentersList);
  const txCentersIds = Array.from(txCentersIdsSet);

  if (txCentersIds) {
    return api.txcenter.getTXCentersList({
      Ids: txCentersIds.join(','),
      pageIndex: 0,
      itemsPerPage: txCentersIds.length,
    });
  }

  return null;
};

export const txCenterQueries = {
  all: () =>
    queryOptions({
      queryKey: txCentersQueryKeys.getList(allTXCentersPaginationParams),
      queryFn: () => api.txcenter.getTXCentersList(allTXCentersPaginationParams),
    }),
  list: (listQuery: PaginatedTXCentersListFilters) =>
    queryOptions({
      queryKey: txCentersQueryKeys.getList(listQuery),
      queryFn: () => api.txcenter.getTXCentersList(listQuery),
      placeholderData: keepPreviousData,
    }),
  infiniteList: (searchPagination: PaginationSearchParams & SearchQuery & SortOrderQuery) =>
    infiniteQueryOptions({
      queryKey: txCentersQueryKeys.getInfiniteList(searchPagination),
      queryFn: ({ pageParam: pageIndex }) => api.txcenter.getTXCentersList({ ...searchPagination, pageIndex }),
      initialPageParam: 0,
      placeholderData: keepPreviousData,
      getNextPageParam: (lastPage) => {
        if (lastPage.pageIndex >= lastPage.totalPages) {
          return undefined;
        }

        return lastPage.pageIndex + 1;
      },
      getPreviousPageParam: (firstPage) => {
        if (firstPage.pageIndex <= 1) {
          return undefined;
        }

        return firstPage.pageIndex - 1;
      },
    }),
  item: (txCenterId?: string) =>
    queryOptions({
      queryKey: txCentersQueryKeys.getItem(txCenterId),
      queryFn: txCenterId ? () => api.txcenter.getTXCenterById(txCenterId) : skipToken,
    }),
  itemWithTXUsers: (txCenterId: string, useEnabledUsers?: boolean) =>
    queryOptions({
      queryKey: txCentersQueryKeys.getItem(txCenterId, useEnabledUsers, BASE_QUERY_KEYS.composed),
      queryFn: () => getTXCenterByIdWithTXUsers(txCenterId, useEnabledUsers),
    }),
  myItem: () =>
    queryOptions({
      queryKey: txCentersQueryKeys.getMyTxCenter(),
      queryFn: getMyTXCenter,
      staleTime: MINUTE_IN_MS * 10,
      enabled:
        !!localStorage.getItem('selectedOrganizationId') &&
        localStorage.getItem('selectedOrganizationPrefix') === ORGANIZATION_TENANT_PREFIX.TxCenter,
    }),
};

const useAllTXCentersListQuery = () => {
  return useQuery(txCenterQueries.all());
};

const useTXCentersListQuery = (listQuery: PaginatedTXCentersListFilters, options: boolean | UseQueryConfig = true) => {
  const enabled = typeof options === 'boolean' ? options : options.enabled;
  return useQuery({ ...txCenterQueries.list(listQuery), enabled });
};
const useMyTxCenterQuery = (options: boolean | UseQueryConfig = true) => {
  const enabled = typeof options === 'boolean' ? options : options.enabled;
  return useQuery({ ...txCenterQueries.myItem(), enabled });
};
const useInfiniteTXCentersListQuery = (filters: SearchQuery & SortOrderQuery) => {
  return useInfiniteQuery(txCenterQueries.infiniteList({ itemsPerPage: 10, pageIndex: 0, ...filters }));
};

const useTXCenterByIdWithTXUsersQuery = (id: string, useEnabledUsers: boolean = true) => {
  return useQuery(txCenterQueries.itemWithTXUsers(id, useEnabledUsers));
};

const useTxCenterByIdQuery = (id: string | null) => {
  return useQuery({
    ...txCenterQueries.item(id as string),
    staleTime: 60 * 1000,
    initialData: () => {
      const query = queryClient.getQueryData(txCenterQueries.all().queryKey);
      return query?.items.find((c) => c.id === id);
    },
    initialDataUpdatedAt: () => queryClient.getQueryState(txCenterQueries.all().queryKey)?.dataUpdatedAt,
    placeholderData: keepPreviousData,
    enabled: !!id,
  });
};

export {
  useAllTXCentersListQuery,
  useTXCentersListQuery,
  useInfiniteTXCentersListQuery,
  useMyTxCenterQuery,
  useTxCenterByIdQuery,
  useTXCenterByIdWithTXUsersQuery,
};
